import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Link, SxProps, Typography } from '@mui/material';
import { GlobalContext } from 'src/common/contexts/globalContext';
import { i18n } from 'src/common/i18n/i18n';
import { CustomDialog } from 'src/common/dialogs';
import { dateFormatter } from 'src/common/utils/formatters';
import { getKeywordsFromWatchLists, getPostActions, multiMarking } from 'src/common/utils/utils';
import { IconDetails } from 'src/common/images';
import { ImagesCarousel, CustomMultiMediaView, LocationTag, ProfileInfo } from 'src/common/components/';
import QuotedPost from './components/QuotedPost/QuotedPost';
import PostInteractions from './components/PostInteractions/PostInteractions';
import _ from 'lodash';
import { ErrorBoundary } from 'react-error-boundary';
import moment from 'moment';
import { Tooltip, SmartImage, COLORS } from '@cellebrite/saas-ui-common';
import PostDialog from 'src/common/dialogs/PostDialog/PostDialog';
import { ReactComponent as VideoIcon } from 'src/common/images/video.svg';
import Popup from 'src/common/components/Popup/Popup';
import IconButton from '@mui/material/IconButton';
import { AddTagIcon } from '@cellebrite/saas-ui-icons';
import TagsManager from 'src/common/components/TagsManager/TagsManager';
import TagsViewer from 'src/common/components/TagsViewer/TagsViewer';
import ReactionsDialog from 'src/common/dialogs/ReactionsDialog/ReactionsDialog';
import MarkedText from 'src/common/components/MarkedText/MarkedText';
import { Element } from 'react-scroll';
import PostComments from 'src/common/components/PostComments/PostComments';
import PostReactions from 'src/common/components/PostReactions/PostReactions';
import MultiMarkedText from 'src/common/components/MultiMarkedText/MultiMarkedText';
import ViewWatchLists from 'src/common/components/ViewWatchLists/ViewWatchLists';

interface ReactionCountData {
    reactionType: string;
    total: number;
    collected: number;
}

export interface PostCardProps {
    post: any;
    section: string;
    onReadMoreClick?: any;
    styles?: SxProps;
    onReactionsClick?: any;
    onCommentsClick?: any;
}

const PostCard: React.FC<PostCardProps> = ({
    post,
    section,
    onReadMoreClick,
    styles,
    onReactionsClick,
    onCommentsClick,
}) => {
    const { dialog, reportData } = useContext(GlobalContext);
    const getBorderColor = () => {
        let result;
        if (reportData.displayHighlights) {
            result =
                post.contentId === reportData.currentHighlightsLink?.id &&
                (reportData.currentHighlightsLink?.type === 'name' ||
                    reportData.currentHighlightsLink?.type === 'id' ||
                    reportData.currentHighlightsLink?.type === 'body' ||
                    reportData.currentHighlightsLink?.type === 'comments')
                    ? COLORS.NEGATIVE
                    : '#D7DCE1';
        } else {
            result =
                post.contentId === reportData.currentFindLink?.id &&
                (reportData.currentFindLink?.type === 'name' ||
                    reportData.currentFindLink?.type === 'id' ||
                    reportData.currentFindLink?.type === 'body')
                    ? COLORS.WARNING_LIGHTEST
                    : '#D7DCE1';
        }
        return result;
    };
    const defaultSx = {
        p: '24px',
        border: `1px solid ${getBorderColor()}`,
        borderRadius: '8px',
        width: '33%',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'space-between',
        position: 'relative',
        cursor: 'pointer',
        height: 'fit-content',
    };
    const contentSx: object = styles ? _.merge(defaultSx, styles) : defaultSx;
    const [finalPost, setFinalPost] = useState(post || null);
    const filteredMedia = finalPost.media?.filter((media) => media.url);
    const [showActions, setShowActions] = useState(false);
    const [actionsFocused, setActionsFocused] = useState(false);
    const [actionsOpen, setActionsOpen] = useState(false);
    const [showReadMore, setShowReadMore] = useState(false);
    const [showTag, setShowTag] = useState(false);
    const [tagOpen, setTagOpen] = useState(false);
    const [tags, setTags] = useState(finalPost.tags || []);
    const getPlatformImage = () => {
        let result;
        if (finalPost.platform) {
            try {
                result = require(`src/common/images/social-icons/${finalPost.platform.toLowerCase()}.svg`);
            } catch (e) {}
        }
        return result;
    };

    const readMoreHandler = () => {
        if (onReadMoreClick) {
            onReadMoreClick(tags);
        } else {
            dialog.open(PostDialog, {
                post: finalPost,
                section: section,
                styles: { width: '762px', height: '762px' },
                onClose: (tags) => setTags(tags),
            });
        }
    };
    const bodyRef = useRef();
    // @ts-ignore
    useEffect(() => setShowReadMore(bodyRef.current?.offsetHeight < bodyRef.current?.scrollHeight), []);
    useEffect(() => setTags(post.tags || []), [post]);
    useEffect(() => {
        const updatedPost = _.cloneDeep(post);
        updatedPost.tags = tags || [];
        setFinalPost(updatedPost);
    }, [tags]);

    function fallbackRender() {
        return <></>;
    }
    const openComments = (event) => {
        if (onCommentsClick) {
            onCommentsClick(event);
        } else if (post.commentsCountData) {
            dialog.open(CustomDialog, {
                styles: { width: '762px', height: '762px' },
                title: (
                    <Box>
                        <Typography variant="s18w700" className="color-bd">
                            {i18n.translate('Comments')}
                        </Typography>
                        <Typography sx={{ mt: 1 }} className="color-md">
                            {i18n.translate('COMMENTS_LABEL', {
                                found: post.commentsCountData?.collected,
                                amount: post.commentsCountData?.total,
                            })}
                        </Typography>
                    </Box>
                ),
                content: <PostComments post={post} />,
            });
            event.stopPropagation();
        }
    };
    const countReaction = (reactionsCountData: ReactionCountData[]): string => {
        const allReactions = _.find(reactionsCountData, { reactionType: 'reactions' });

        return i18n.translate('REACTIONS_LABEL', {
            collected: allReactions?.collected,
            total: allReactions?.total,
        });
    };

    const getObject = (type) => {
        const obj = {};
        obj[`& mark:nth-of-type(${reportData.currentFindLink?.index + 1})`] =
            post.contentId === reportData.currentFindLink?.id &&
            type === reportData.currentFindLink?.type &&
            reportData.currentFindLink?.section === section
                ? { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` }
                : null;
        return obj;
    };

    const showPostDate = (): JSX.Element => {
        const tolltipContent = !moment(finalPost.publishDate).isValid() ? (
            <Typography>{`Relative to the search date - ${dateFormatter(finalPost.collectionTime)}`}</Typography>
        ) : finalPost.editTimePublished ? (
            <Typography>{i18n.translate('ADDED_DATE', { date: dateFormatter(finalPost.publishDate) })}</Typography>
        ) : null;

        const displayedDate = (
            <Typography
                sx={{
                    fontSize: '12px',
                    mt: 2,
                    color: COLORS.MUTED_DARK,
                    cursor: 'pointer',
                    display: 'inline-flex',
                }}
            >
                {finalPost.editTimePublished
                    ? dateFormatter(finalPost.editTimePublished)
                    : dateFormatter(finalPost.publishDate)}
            </Typography>
        );

        return tolltipContent ? <Tooltip content={tolltipContent}>{displayedDate}</Tooltip> : displayedDate;
    };
    const postKeywords = getKeywordsFromWatchLists(post.watchlist, reportData.currentHighlightsWatchlist?.id);
    return (
        <ErrorBoundary fallbackRender={fallbackRender}>
            <Box
                sx={contentSx}
                onMouseEnter={() => {
                    setShowTag(true);
                    setShowActions(true);
                }}
                onMouseLeave={() => {
                    setShowTag(false);
                    setShowActions(false);
                }}
                className="background-color-hover-mho"
                onClick={actionsFocused ? null : readMoreHandler}
            >
                <Element style={{ position: 'absolute' }} name={post.contentId + section}></Element>
                <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img width="16" height="16" src={getPlatformImage()} alt="" />
                        {finalPost.title && (
                            <Typography sx={{ ml: 1 }} variant="s14w500">
                                {finalPost.platform}
                            </Typography>
                        )}
                        {finalPost.titleComment && (
                            <Typography sx={{ ml: 1, color: COLORS.MUTED_DARK }}>{finalPost.titleComment} </Typography>
                        )}
                    </Box>
                    {finalPost.headline?.includes(' replied on this') ? (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ lineHeight: '16px', backgroundColor: COLORS.MUTED_SOFT }}>
                                {finalPost.headline?.split(' replied on this')[0]}
                            </Typography>
                            <Link
                                sx={{ pl: 0.5, pr: 0.5, backgroundColor: COLORS.MUTED_SOFT, lineHeight: '16px' }}
                                onClick={(event) => openComments(event)}
                            >
                                {i18n.translate('replied')}
                            </Link>
                            <Typography sx={{ lineHeight: '16px', backgroundColor: COLORS.MUTED_SOFT }}>
                                {i18n.translate('on this')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                            <Typography
                                sx={{
                                    lineHeight: '16px',
                                    height: 16,
                                    backgroundColor: COLORS.MUTED_SOFT,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {finalPost.headline}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
                        {reportData.collection?.taggable && <TagsViewer checkedTags={tags} styles={{ mb: 1 }} />}
                        {!!finalPost.watchlist?.length && <ViewWatchLists watchLists={finalPost.watchlist} />}
                        <ProfileInfo
                            styles={{ mt: 2, whiteSpace: 'nowrap', width: '100%' }}
                            key={finalPost.title}
                            imageSrc={finalPost.profileInfo.imageSrc}
                            topInfo={
                                reportData.displayHighlights ? (
                                    <MultiMarkedText
                                        containerProps={{
                                            variant: 's14w500',
                                            sx: multiMarking(
                                                finalPost.contentId,
                                                section,
                                                'name',
                                                reportData.currentHighlightsLink,
                                            ),
                                        }}
                                        searchTextArr={postKeywords}
                                    >
                                        {finalPost.profileInfo.name}
                                    </MultiMarkedText>
                                ) : (
                                    <MarkedText
                                        containerProps={{
                                            variant: 's14w500',
                                            sx: getObject('name'),
                                        }}
                                        searchText={reportData.find}
                                    >
                                        {finalPost.profileInfo.name}
                                    </MarkedText>
                                )
                            }
                            bottomInfo={
                                reportData.displayHighlights ? (
                                    <MultiMarkedText
                                        containerProps={{
                                            sx: multiMarking(
                                                finalPost.contentId,
                                                section,
                                                'id',
                                                reportData.currentHighlightsLink,
                                            ),
                                        }}
                                        searchTextArr={postKeywords}
                                    >
                                        {finalPost.profileInfo.id}
                                    </MultiMarkedText>
                                ) : (
                                    <MarkedText
                                        containerProps={{
                                            sx: getObject('id'),
                                        }}
                                        searchText={reportData.find}
                                    >
                                        {finalPost.profileInfo.id}
                                    </MarkedText>
                                )
                            }
                        />
                    </Box>
                    {showPostDate()}
                    <Box sx={{ display: 'flex', mt: 0.5 }}>
                        {finalPost.bodyTitle ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', height: 42, width: '100%' }}>
                                {reportData.displayHighlights ? (
                                    <MultiMarkedText
                                        containerProps={{
                                            sx: Object.assign(
                                                {
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: '1',
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    fontWeight: 500,
                                                },
                                                multiMarking(
                                                    finalPost.contentId,
                                                    section,
                                                    'bodyTitle',
                                                    reportData.currentHighlightsLink,
                                                ),
                                            ),
                                            title: finalPost.bodyTitle,
                                            ref: bodyRef,
                                            noWrap: false,
                                        }}
                                        searchTextArr={postKeywords}
                                    >
                                        {finalPost.bodyTitle}
                                    </MultiMarkedText>
                                ) : (
                                    <MarkedText
                                        containerProps={{
                                            sx: Object.assign(
                                                {
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: '1',
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    fontWeight: 500,
                                                },
                                                getObject('bodyTitle'),
                                            ),
                                            title: finalPost.bodyTitle,
                                            ref: bodyRef,
                                            noWrap: false,
                                        }}
                                        searchText={reportData.find}
                                    >
                                        {finalPost.bodyTitle}
                                    </MarkedText>
                                )}
                                {reportData.displayHighlights ? (
                                    <MultiMarkedText
                                        containerProps={{
                                            sx: Object.assign(
                                                {
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: '1',
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                },
                                                multiMarking(
                                                    finalPost.contentId,
                                                    section,
                                                    'body',
                                                    reportData.currentHighlightsLink,
                                                ),
                                            ),
                                            title: finalPost.body,
                                            ref: bodyRef,
                                            noWrap: false,
                                        }}
                                        searchTextArr={postKeywords}
                                    >
                                        {finalPost.body}
                                    </MultiMarkedText>
                                ) : reportData.displayHighlights ? (
                                    <MultiMarkedText
                                        containerProps={{
                                            sx: Object.assign(
                                                {
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: '1',
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                },
                                                multiMarking(
                                                    finalPost.contentId,
                                                    section,
                                                    'body',
                                                    reportData.currentHighlightsLink,
                                                ),
                                            ),
                                            title: finalPost.body,
                                            ref: bodyRef,
                                            noWrap: false,
                                        }}
                                        searchTextArr={postKeywords}
                                    >
                                        {finalPost.body}
                                    </MultiMarkedText>
                                ) : (
                                    <MarkedText
                                        containerProps={{
                                            sx: Object.assign(
                                                {
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: '1',
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                },
                                                getObject('body'),
                                            ),
                                            title: finalPost.body,
                                            ref: bodyRef,
                                            noWrap: false,
                                        }}
                                        searchText={reportData.find}
                                    >
                                        {finalPost.body}
                                    </MarkedText>
                                )}
                            </Box>
                        ) : reportData.displayHighlights ? (
                            <MultiMarkedText
                                containerProps={{
                                    sx: Object.assign(
                                        {
                                            display: '-webkit-box',
                                            WebkitLineClamp: '2',
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            height: '42px',
                                        },
                                        multiMarking(
                                            finalPost.contentId,
                                            section,
                                            'body',
                                            reportData.currentHighlightsLink,
                                        ),
                                    ),
                                    title: finalPost.body,
                                    ref: bodyRef,
                                    noWrap: false,
                                }}
                                searchTextArr={postKeywords}
                            >
                                {finalPost.body}
                            </MultiMarkedText>
                        ) : (
                            <MarkedText
                                containerProps={{
                                    sx: Object.assign(
                                        {
                                            display: '-webkit-box',
                                            WebkitLineClamp: '2',
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            height: '42px',
                                        },
                                        getObject('body'),
                                    ),
                                    title: finalPost.body,
                                    ref: bodyRef,
                                    noWrap: false,
                                }}
                                searchText={reportData.find}
                            >
                                {finalPost.body}
                            </MarkedText>
                        )}
                    </Box>
                    <Link sx={{ fontSize: '13px', visibility: showReadMore ? 'visible' : 'hidden' }}>
                        {i18n.translate('Read more')}
                    </Link>
                    {!finalPost.quotedPost && !!filteredMedia?.length && (
                        <Box
                            sx={{
                                height: 260,
                                display: 'flex',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                borderRadius: '8px',
                            }}
                        >
                            {filteredMedia.length === 1 ? (
                                <ImagesCarousel
                                    keepFailedImages={true}
                                    items={[
                                        {
                                            src: filteredMedia[0].url,
                                            label: finalPost.location?.address,
                                            hideLabel: true,
                                            imageProps: {
                                                containerStyles: { height: 260 },
                                                roundedCorners: true,
                                                contain: true,
                                                videoType: filteredMedia[0].type === 'Video',
                                                elementInImage: finalPost.location?.address && (
                                                    <LocationTag address={finalPost.location.address} />
                                                ),
                                            },
                                        },
                                    ]}
                                />
                            ) : (
                                <CustomMultiMediaView
                                    media={filteredMedia}
                                    roundedCorners={true}
                                    elementInImage={
                                        finalPost.location?.address && (
                                            <LocationTag address={finalPost.location.address} />
                                        )
                                    }
                                />
                            )}
                        </Box>
                    )}
                    {!!finalPost.quotedPosts?.length && (
                        <QuotedPost post={finalPost.quotedPosts[0]} imageContainerHeight={260} section={section} />
                    )}
                    {!finalPost.quotedPost && !filteredMedia?.length && !!finalPost.sharedLink?.attachments?.length && (
                        <Box
                            sx={{
                                height: 260,
                                overflow: 'hidden',
                                border: `1px solid ${COLORS.MUTED_LIGHTEST}`,
                                borderRadius: '8px',
                            }}
                        >
                            <Box
                                sx={{
                                    height: 140,
                                }}
                            >
                                <SmartImage
                                    cover={true}
                                    src={finalPost.sharedLink.attachments[0].urls?.[0]}
                                    centerOverlay={
                                        finalPost.sharedLink.attachments[0].title === 'Video' ? (
                                            <Box
                                                style={{
                                                    borderRadius: 50,
                                                    width: 86,
                                                    height: 86,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                                className="fill-l background-color-bd-opacity"
                                            >
                                                <VideoIcon />
                                            </Box>
                                        ) : null
                                    }
                                />
                            </Box>
                            <Box
                                sx={{
                                    height: 120,
                                    p: 1,
                                    pb: 2,
                                    backgroundColor: COLORS.MUTED_SOFT,
                                }}
                            >
                                <Typography variant="s12w400" className="color-md">
                                    {finalPost.sharedLink.site}
                                </Typography>
                                <Typography variant="s16w500" className="color-mh">
                                    {finalPost.sharedLink.headline}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {!filteredMedia?.length &&
                        !finalPost.quotedPost &&
                        !!finalPost.sharedLink?.attachments?.length &&
                        finalPost.location?.address && (
                            <Box sx={{ alignSelf: 'end', height: 40 }}>
                                {finalPost.location?.address && <LocationTag address={finalPost.location.address} />}
                            </Box>
                        )}

                    <PostInteractions
                        post={finalPost}
                        onReactionsClick={(event) => {
                            if (onReactionsClick) {
                                onReactionsClick(event);
                            } else {
                                dialog.open(ReactionsDialog, {
                                    reactionCounts: countReaction(finalPost.reactionsCountData),
                                    content: (
                                        <PostReactions
                                            post={finalPost}
                                            styles={{
                                                '& .tab-content': { height: '585px', overflow: 'scroll' },
                                            }}
                                        />
                                    ),
                                });
                                event.stopPropagation();
                            }
                        }}
                        onCommentsClick={openComments}
                    />
                </Box>
                {(showActions || actionsOpen || showTag || tagOpen) && (
                    <Box onMouseEnter={() => setActionsFocused(true)} onMouseLeave={() => setActionsFocused(false)}>
                        {getPostActions(
                            finalPost,
                            { position: 'absolute' },
                            [
                                {
                                    name: i18n.translate('Show Details'),
                                    icon: <IconDetails />,
                                    onClick: readMoreHandler ? readMoreHandler : () => {},
                                },
                            ],
                            () => setActionsOpen(!actionsOpen),
                            reportData.collection?.taggable ? (
                                <Box sx={{ ml: 1, mr: 0.5, minWidth: '24px' }}>
                                    {(showTag || tagOpen) && (
                                        <Popup
                                            onToggle={() => {
                                                setTagOpen(!tagOpen);
                                            }}
                                            actionElement={(handleOpen, anchorRef, open) => {
                                                return (
                                                    <Box ref={anchorRef}>
                                                        <IconButton
                                                            sx={{
                                                                'backgroundColor': tagOpen
                                                                    ? COLORS.MUTED_DOUBLE_HOVER
                                                                    : null,
                                                                '&:hover': {
                                                                    backgroundColor: COLORS.MUTED_DOUBLE_HOVER,
                                                                },
                                                            }}
                                                            disableRipple
                                                            onClick={handleOpen}
                                                        >
                                                            <AddTagIcon
                                                                fill={open ? COLORS.BRAND : COLORS.MUTED_DARK}
                                                            />
                                                        </IconButton>
                                                    </Box>
                                                );
                                            }}
                                            contentElement={(handleClose) => (
                                                <Box>
                                                    <TagsManager
                                                        setTags={setTags}
                                                        checkedTags={tags}
                                                        section={section}
                                                        elementId={finalPost.contentId}
                                                    />
                                                </Box>
                                            )}
                                        />
                                    )}
                                </Box>
                            ) : null,
                        )}
                    </Box>
                )}
            </Box>
        </ErrorBoundary>
    );
};

export default PostCard;
